import { useMemo, useState } from "react";
import { Inbox } from "lucide-react";
import type { EmailCohort, EmailMessage } from "./api";
import MessageReview from "./message-review";
import { Panel, StatusPill, fmtDate } from "./shared";

const FILTERS = [
  { id: "review", label: "Needs review", match: (m: EmailMessage) => m.status === "draft" || m.status === "needs_attention" },
  { id: "live", label: "Approved and live", match: (m: EmailMessage) => ["approved", "sending", "paused"].includes(m.status) },
  { id: "done", label: "Finished", match: (m: EmailMessage) => m.status === "completed" || m.status === "cancelled" },
  { id: "all", label: "All", match: () => true },
] as const;

export default function QueuePanel({ messages, cohorts, selectedId, onSelect, onChanged }: {
  messages: EmailMessage[]; cohorts: EmailCohort[]; selectedId: number | null; onSelect: (id: number | null) => void; onChanged: () => void;
}) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("review");
  const list = useMemo(() => messages.filter(FILTERS.find((f) => f.id === filter)!.match), [messages, filter]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)]">
      <div className={`space-y-3 ${selectedId ? "hidden lg:block" : ""}`}>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => {
            const n = messages.filter(f.match).length;
            return <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)} className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === f.id ? "border-[#FF5C00] bg-[#FF5C00]/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>{f.label} <span className="font-mono">{n}</span></button>;
          })}
        </div>
        {list.length === 0 ? (
          <Panel className="p-10 text-center"><Inbox className="mx-auto w-8 h-8 text-muted-foreground/50" /><p className="mt-2 text-sm text-muted-foreground">Nothing here. Drafts from the assistant or compose form appear in Needs review.</p></Panel>
        ) : (
          <ul className="space-y-1.5">
            {list.map((m) => (
              <li key={m.id}>
                <button data-testid={`row-message-${m.id}`} onClick={() => onSelect(m.id)} className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${selectedId === m.id ? "border-[#FF5C00] bg-[#FF5C00]/[0.05]" : "border-border bg-card hover:border-[#FF5C00]/40"}`}>
                  <div className="flex items-start justify-between gap-2"><p className="font-medium leading-snug line-clamp-2">{m.subject}</p><StatusPill status={m.status} /></div>
                  <p className="mt-1 text-xs text-muted-foreground">{m.source}{m.essential ? " · essential" : ""} · {m.total} recipient{m.total === 1 ? "" : "s"} · {fmtDate(m.scheduled_at || m.updated_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Panel className={`p-5 ${selectedId ? "" : "hidden lg:block"}`}>
        {selectedId ? (
          <>
            <button data-testid="button-back-queue" onClick={() => onSelect(null)} className="lg:hidden mb-4 text-sm text-muted-foreground">Back to list</button>
            <MessageReview id={selectedId} cohorts={cohorts} onChanged={onChanged} />
          </>
        ) : <p className="py-20 text-center text-sm text-muted-foreground">Select a message to review its exact content and recipients.</p>}
      </Panel>
    </div>
  );
}
